import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../../components/AppShell";
import T from "../../components/T";
import { Button } from "../../components/ui/button";
import { ChevronLeft, Wind } from "lucide-react";
import { useContent } from "../../hooks/useContent";
import { api } from "../../lib/api";
import { lightTap, successPulse } from "../../lib/haptics";

/**
 * Breathing tools — restored from Build 5.
 *
 * Patterns (3): Box Breath, Long Exhale ("Long cycle"), Sigh of Relief.
 * Each renders an animated dual-ring orb whose label switches between
 * Inhale / Hold / Exhale on a real-time timer.
 *
 * Haptic feedback: a light tap on every phase change so the cue is felt
 * as well as seen; a single Success notification when the user finishes
 * all the cycles of the pattern. No-op on web.
 */

function BreathOrb({ pattern, onClose }) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState(t("sanctuary.phase_inhale"));
  const [count, setCount] = useState(pattern.cycles);
  const [running, setRunning] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!running) return;
    const phaseLabel = (p) =>
      p === "Inhale" ? t("sanctuary.phase_inhale") :
      p === "Hold"   ? t("sanctuary.phase_hold")   :
                       t("sanctuary.phase_exhale");

    const order =
      pattern.id === "box"
        ? [{ phase: "Inhale", s: 4 }, { phase: "Hold", s: 4 }, { phase: "Exhale", s: 4 }, { phase: "Hold", s: 4 }]
        : pattern.id === "long_exhale"
        ? [{ phase: "Inhale", s: 4 }, { phase: "Exhale", s: 8 }]
        : [{ phase: "Inhale", s: 2 }, { phase: "Inhale", s: 2 }, { phase: "Exhale", s: 6 }];

    let cycle = 0;
    let idx = 0;
    const run = () => {
      setPhase(phaseLabel(order[idx].phase));
      lightTap();
      timerRef.current = setTimeout(() => {
        idx++;
        if (idx >= order.length) {
          idx = 0;
          cycle++;
          setCount(pattern.cycles - cycle);
        }
        if (cycle >= pattern.cycles) {
          setRunning(false);
          // Single gentle confirmation when the whole pattern is done.
          successPulse();
          api.post("/activity", { kind: "breath" }).catch(() => {});
          return;
        }
        run();
      }, order[idx].s * 1000);
    };
    run();
    return () => clearTimeout(timerRef.current);
  }, [pattern, running, t]);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-background/95 backdrop-blur-xl"
      data-testid="breath-overlay"
    >
      <div className="text-center px-8">
        <p className="font-script text-2xl text-brand">{pattern.name}</p>
        <div className="relative my-10 mx-auto w-56 h-56 grid place-items-center">
          <div className="absolute inset-0 rounded-full bg-brand/15 animate-breathe" />
          <div
            className="absolute inset-6 rounded-full bg-brand/25 animate-breathe"
            style={{ animationDelay: "1s" }}
          />
          <div className="relative font-serif text-4xl italic text-brand">{phase}</div>
        </div>
        <p className="text-foreground/65">{t("sanctuary.cycles_left", { n: count })}</p>
        <Button
          data-testid="breath-close-btn"
          onClick={onClose}
          className="mt-8 rounded-full bg-brand hover:bg-brand/90 text-white px-8 h-12"
        >
          {running ? t("sanctuary.im_okay_close") : t("common.done")}
        </Button>
      </div>
    </div>
  );
}

export default function Breath() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { SANCTUARY } = useContent();
  const patterns = SANCTUARY?.breathing || [];
  const [active, setActive] = useState(null);

  return (
    <AppShell>
      <div className="px-8 pt-16 pb-6" data-testid="page-breath">
        <button
          onClick={() => navigate("/sanctuary")}
          className="flex items-center gap-1 text-sm text-foreground/55 hover:text-brand mb-2"
        >
          <ChevronLeft className="w-4 h-4" /> {t("v11.back_to_sanctuary")}
        </button>
        <p className="font-script text-2xl text-brand">{t("sanctuary.breath")}</p>
        <h1 className="font-serif text-4xl leading-tight"><T i18nKey="v11.breath.h" /></h1>
        <p className="mt-3 text-foreground/70 leading-relaxed">{t("v11.breath.intro")}</p>
      </div>

      <div className="px-8 pb-10 space-y-3">
        {patterns.map((b) => (
          <button
            key={b.id || b.name}
            data-testid={`breath-${b.id || b.name.replace(/\s+/g, "-").toLowerCase()}`}
            onClick={() => setActive(b)}
            className="w-full text-left rounded-3xl bg-card border border-border p-5 hover:border-brand/50 transition-all flex items-start gap-3"
          >
            <span className="w-10 h-10 grid place-items-center rounded-full bg-brand/10 text-brand shrink-0">
              <Wind className="w-5 h-5" />
            </span>
            <div>
              <div className="font-serif text-xl">{b.name}</div>
              <p className="text-sm text-foreground/65 mt-1">{b.description}</p>
              <p className="text-xs text-brand mt-2">{t("sanctuary.cycles", { n: b.cycles })}</p>
            </div>
          </button>
        ))}
      </div>

      {active && <BreathOrb pattern={active} onClose={() => setActive(null)} />}
    </AppShell>
  );
}
